"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  CountGrid,
  parseCounts,
  replaceAt,
  replaceCell,
} from "@/components/tools/stats-tests/count-grid";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import {
  type McNemarMethod,
  McNemarResults,
} from "@/components/tools/stats-tests/mcnemar-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { mcnemar } from "@/lib/tool-kit/stats-tests/mcnemar";

/** English strings. A translated content file overrides these through `ui`. */
export const MCNEMAR_TEST_CALCULATOR_STRINGS = {
  tableHint:
    "Rows are the first measurement, columns the second. Each count is a number of people (or pairs), so every person appears exactly once.",
  cell: "Count for {row}, {column}",
  rowName: "Name of row {n}",
  columnName: "Name of column {n}",
  total: "Total",
  rowYes: "First: yes",
  rowNo: "First: no",
  columnYes: "Second: yes",
  columnNo: "Second: no",
  methodLabel: "p-value to report",
  methodExact: "Exact binomial (recommended)",
  methodCorrected: "χ² with continuity correction (Edwards)",
  methodUncorrected: "χ² without correction",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level",
  tryExample: "Try an example",
  exampleRows: "Before: support|Before: oppose",
  exampleColumns: "After: support|After: oppose",
  exampleCounts: "30, 15, 5, 50",
  results: "Results",
  pExact: "p-value (exact binomial)",
  chiCorrected: "χ² (corrected)",
  chiUncorrected: "χ² (uncorrected)",
  pCorrected: "p (corrected χ²)",
  pUncorrected: "p (uncorrected χ²)",
  oddsRatio: "Odds ratio b / c",
  ci: "{level} exact CI for the odds ratio",
  discordant: "Discordant pairs (b + c)",
  proportions: "Proportion “yes”: first, second",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the proportion changed between the two measurements.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the proportion changed.",
  discordantNote:
    "Only the {b} + {c} people who changed answers carry information; the {a} + {d} who gave the same answer twice do not affect the test.",
  smallDiscordant:
    "With fewer than 25 discordant pairs, the chi-square approximation is rough — report the exact binomial p-value.",
  methodsHeading: "All three p-values",
  methodColumn: "Method",
  statisticColumn: "Statistic",
  pColumn: "p",
  howCalculated: "How this was calculated",
  workingExact:
    "Exact: under H₀, b ~ Binomial(b + c, ½). With b + c = {n}, p = min(1, 2 × P(X ≤ {min})) = {p}.",
  workingCi:
    "The interval is Clopper–Pearson for π = b / (b + c) = {pi}, giving [{piLow}, {piHigh}], converted to odds with π / (1 − π).",
  apaHeading: "APA-style result",
  apaExact:
    "McNemar's test (exact binomial), *p* {p}, *OR* = {or}, {level} CI [{low}, {high}]",
  apaChi:
    "McNemar's χ²(1, *N* = {n}) = {chi}, *p* {p}, *OR* = {or}, {level} CI [{low}, {high}]",
  apaNote:
    "Add the two proportions being compared, for example the percentage answering yes at each measurement.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Fill all four cells with whole-number counts — zero is allowed.",
  errorNoDiscordant:
    "b and c are both zero: nobody changed, so there is no evidence either way and the test cannot be computed.",
} as const;

const SPLIT = "|";

export default function McnemarTestCalculatorWidget() {
  const strings = useToolUi(MCNEMAR_TEST_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [rowLabels, setRowLabels] = useState<string[]>(() => [
    strings.rowYes,
    strings.rowNo,
  ]);
  const [colLabels, setColLabels] = useState<string[]>(() => [
    strings.columnYes,
    strings.columnNo,
  ]);
  const [cells, setCells] = useState<string[][]>([
    ["", ""],
    ["", ""],
  ]);
  const [method, setMethod] = useState<McNemarMethod>("exact");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const table = parseCounts(cells);
  const result = table
    ? mcnemar(table[0][0], table[0][1], table[1][0], table[1][1], Number(level))
    : null;
  const methods: Option<McNemarMethod>[] = [
    { value: "exact", label: strings.methodExact },
    { value: "corrected", label: strings.methodCorrected },
    { value: "uncorrected", label: strings.methodUncorrected },
  ];

  function fillExample() {
    const [a = "", b = "", c = "", d = ""] = strings.exampleCounts
      .split(",")
      .map((value) => value.trim());
    setCells([
      [a, b],
      [c, d],
    ]);
    setRowLabels(strings.exampleRows.split(SPLIT));
    setColLabels(strings.exampleColumns.split(SPLIT));
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className={HINT}>{strings.tableHint}</p>
        <CountGrid
          colLabels={colLabels}
          fmt={fmt}
          idPrefix="mcn"
          onCell={(i, j, value) => setCells(replaceCell(cells, i, j, value))}
          onColLabel={(j, value) =>
            setColLabels(replaceAt(colLabels, j, value))
          }
          onRowLabel={(i, value) =>
            setRowLabels(replaceAt(rowLabels, i, value))
          }
          rowLabels={rowLabels}
          text={strings}
          values={cells}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="mcn-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={methods}
          value={method}
        />
        <SelectField
          id="mcn-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="mcn-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {result ? (
        <McNemarResults
          alpha={Number(alpha)}
          fmt={fmt}
          method={method}
          r={result}
          strings={strings}
        />
      ) : (
        <Notice>{table ? strings.errorNoDiscordant : strings.prompt}</Notice>
      )}
    </div>
  );
}
